import type { z } from 'zod';
import {
  CompleteStripeConnectedAccountOnboardingInputSchema,
  EnsureStripeCustomerInputSchema,
  EnsureStripeCustomerResponseSchema,
  HostedPaymentSessionInputSchema,
  HostedPaymentSessionResponseSchema,
  HostedPortalSessionInputSchema,
  HostedPortalSessionResponseSchema,
  PaymentManagementContextSchema,
  PaymentControlPlaneSchema,
  PaymentControlPlaneReadinessSchema,
  PaymentControlPlaneUpdateInputSchema,
  PaymentEventEnvelopeSchema,
  PaymentMirrorLedgerEntrySchema,
  PaymentMirrorLedgerListResponseSchema,
  StartStripeConnectedAccountOnboardingInputSchema,
  StripeConnectedAccountOnboardingSessionSchema,
} from '@triedge/domain-core';
import {
  toAppSyncMutationName,
  toAppSyncOperationName,
  toRtkGlueModuleId,
  toSemanticRouteId,
  type AppSyncMutationName,
  type AppSyncOperationName,
  type RtkGlueModuleId,
  type SemanticRouteId,
} from './semantic-types';

type PaymentsQueryContract = {
  operationName: AppSyncOperationName;
  routeId: SemanticRouteId;
  outputSchema: z.ZodTypeAny;
};

type PaymentsMutationContract = {
  mutationName: AppSyncMutationName;
  routeId: SemanticRouteId;
  inputSchema: z.ZodTypeAny;
  outputSchema: z.ZodTypeAny;
};

export const PAYMENTS_MODULE_ID: RtkGlueModuleId = toRtkGlueModuleId('payments');

export const PAYMENTS_ROUTE_IDS = Object.freeze({
  schoolControlPlane: toSemanticRouteId('payments-school-control-plane'),
  donationsPublicCampaign: toSemanticRouteId('donations-public-campaign'),
  tipATeacherParentCatalog: toSemanticRouteId('tip-a-teacher-parent-catalog'),
  campsParentCatalog: toSemanticRouteId('camps-parent-catalog'),
});

export const PAYMENTS_QUERY_CONTRACTS = Object.freeze({
  getPaymentControlPlane: {
    operationName: toAppSyncOperationName('getPaymentControlPlane'),
    routeId: PAYMENTS_ROUTE_IDS.schoolControlPlane,
    outputSchema: PaymentControlPlaneSchema,
  },
  getPaymentControlPlaneReadiness: {
    operationName: toAppSyncOperationName('getPaymentControlPlaneReadiness'),
    routeId: PAYMENTS_ROUTE_IDS.schoolControlPlane,
    outputSchema: PaymentControlPlaneReadinessSchema,
  },
  getMyPaymentManagementContext: {
    operationName: toAppSyncOperationName('getMyPaymentManagementContext'),
    routeId: PAYMENTS_ROUTE_IDS.schoolControlPlane,
    outputSchema: PaymentManagementContextSchema,
  },
  listPaymentMirrorLedgerEntries: {
    operationName: toAppSyncOperationName('listPaymentMirrorLedgerEntries'),
    routeId: PAYMENTS_ROUTE_IDS.schoolControlPlane,
    outputSchema: PaymentMirrorLedgerListResponseSchema,
  },
} as const satisfies Record<string, PaymentsQueryContract>);

export const PAYMENTS_MUTATION_CONTRACTS = Object.freeze({
  updatePaymentControlPlane: {
    mutationName: toAppSyncMutationName('updatePaymentControlPlane'),
    routeId: PAYMENTS_ROUTE_IDS.schoolControlPlane,
    inputSchema: PaymentControlPlaneUpdateInputSchema,
    outputSchema: PaymentControlPlaneSchema,
  },
  startStripeConnectedAccountOnboarding: {
    mutationName: toAppSyncMutationName('startStripeConnectedAccountOnboarding'),
    routeId: PAYMENTS_ROUTE_IDS.schoolControlPlane,
    inputSchema: StartStripeConnectedAccountOnboardingInputSchema,
    outputSchema: StripeConnectedAccountOnboardingSessionSchema,
  },
  completeStripeConnectedAccountOnboarding: {
    mutationName: toAppSyncMutationName('completeStripeConnectedAccountOnboarding'),
    routeId: PAYMENTS_ROUTE_IDS.schoolControlPlane,
    inputSchema: CompleteStripeConnectedAccountOnboardingInputSchema,
    outputSchema: PaymentControlPlaneSchema,
  },
  ensureStripeCustomer: {
    mutationName: toAppSyncMutationName('ensureStripeCustomer'),
    routeId: PAYMENTS_ROUTE_IDS.schoolControlPlane,
    inputSchema: EnsureStripeCustomerInputSchema,
    outputSchema: EnsureStripeCustomerResponseSchema,
  },
  createHostedPaymentSession: {
    mutationName: toAppSyncMutationName('createHostedPaymentSession'),
    routeId: PAYMENTS_ROUTE_IDS.donationsPublicCampaign,
    inputSchema: HostedPaymentSessionInputSchema,
    outputSchema: HostedPaymentSessionResponseSchema,
  },
  createHostedPortalSession: {
    mutationName: toAppSyncMutationName('createHostedPortalSession'),
    routeId: PAYMENTS_ROUTE_IDS.schoolControlPlane,
    inputSchema: HostedPortalSessionInputSchema,
    outputSchema: HostedPortalSessionResponseSchema,
  },
  recordPaymentEventEnvelope: {
    mutationName: toAppSyncMutationName('recordPaymentEventEnvelope'),
    routeId: PAYMENTS_ROUTE_IDS.schoolControlPlane,
    inputSchema: PaymentEventEnvelopeSchema,
    outputSchema: PaymentMirrorLedgerEntrySchema,
  },
} as const satisfies Record<string, PaymentsMutationContract>);

export type PaymentsQueryContractName = keyof typeof PAYMENTS_QUERY_CONTRACTS;
export type PaymentsMutationContractName = keyof typeof PAYMENTS_MUTATION_CONTRACTS;
